import {
  TOP_LEVEL_KEYS,
  SOURCE_KINDS,
  SCORE_SPEC_SOURCE_KIND,
  SPEC_ID_REGEX,
  RESERVED_FEED_COLUMNS,
  SCORE_NAME_REGEX,
  RESERVED_SCORE_NAME_REGEX,
  FEED_NAME_REGEX,
  FEED_NAME_SEPARATOR,
  REASONING_EFFORTS,
  CONTEXT_SETTING_KEYS,
  CONTEXT_SETTING_VALUES,
  RELATIONSHIP_KINDS,
  SCHEMA_ALLOWED_KEYS,
  SCHEMA_ALLOWED_TYPES,
  KINDS_WITH_DATE_RANGE,
  KINDS_WITH_DOC_TYPE_RULES,
  KINDS_WITH_DOC_TYPE_STRINGS,
  DOCUMENT_KIND_PARAMS,
  providesFor,
  kindEntry,
  isKnown,
  isSupported,
  isAnchorToken,
  isValidDateToken,
  isAbsoluteToken,
  runVariablesFor,
} from './specSchema';
import { matchCount } from './jsonPath';
import { unknownVariables } from './jinjaVars';
import { isProseRetired, proseDeprecationRow } from './proseContract';

// A client-side mirror of the loader's load-time rules.
//
// THIS NEVER BLOCKS ANYTHING. It returns a list; the UI counts it, ranks it, and puts a [Go →] on
// each row. Save stays lenient because the server's create/update is lenient. Lock stays available
// because these tables are a mirror that decays — the day the backend enables a kind, a spec that
// this file calls broken is legal, and refusing to lock it would strand it permanently.
//
// severity:
//   'error'  — a server tier will reject this. Mirrors a documented rule.
//   'advice' — house guidance or a footgun. NOT a rule either tier enforces; worded as such.
//
// tier — WHICH server gate rejects an error, because the two do not agree and the difference is
// load-bearing:
//   'lock' — the compile rejects it at lock. The draft still saves.
//   'save' — create/PATCH reject it with a 422, so the draft cannot even be stored.
// The lenient/strict asymmetry is real: since PR #455 the CRUD tier rejects a spec that declares
// BOTH output contracts, while the runtime loader tolerates both so already-approved legacy specs
// keep loading. A both-set row is therefore a save-tier error and NEVER a lock-tier one — calling it
// a compile failure would flag legal, locked, production specs as broken.
//
// `panel` routes the [Go →] button to the question panel that owns the path.

export const SEVERITY = { ERROR: 'error', ADVICE: 'advice' };
export const TIER = { SAVE: 'save', LOCK: 'lock' };

const PANEL = {
  IDENTITY: 'identity',
  TRIGGER: 'trigger',
  DOCUMENT_SET: 'document_set',
  SCORE_GEN: 'score_gen',
  OUTPUT: 'output_extraction',
};

export const PROBLEM_PANELS = PANEL;

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// The JSON Schema type a literal value would satisfy. `integer` rather than `number` for whole
// numbers, so a member of an `integer` node is judged against what it actually is; the caller
// widens integer -> number, never the other way.
const jsonTypeOf = (value) => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number';
  if (typeof value === 'object') return 'object';
  return typeof value;
};

// ---------------------------------------------------------------------------
// output_schema — OpenAI strict-mode subset
// ---------------------------------------------------------------------------
const validateSchemaNode = (node, path, push) => {
  if (!isObject(node)) {
    push({
      path,
      severity: SEVERITY.ERROR,
      panel: PANEL.OUTPUT,
      title: 'Schema node must be an object',
      explain: 'Every node of output_schema is a JSON Schema object.',
    });
    return;
  }

  Object.keys(node).forEach((key) => {
    if (SCHEMA_ALLOWED_KEYS.indexOf(key) === -1) {
      push({
        path: `${path}.${key}`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: `\`${key}\` is not allowed in strict mode`,
        explain: `Strict mode allows only ${SCHEMA_ALLOWED_KEYS.join(', ')}. Anything else 400s the scoring request.`,
      });
    }
  });

  const types = Array.isArray(node.type) ? node.type : [node.type];

  // `enum` is grammar-enforced by the model, so it is the one strict-mode key that constrains a
  // VALUE rather than a shape — and the loader holds its members to the node's declared type. A
  // mismatch fails strict validation, which is a refusal at lock.
  if (node.enum !== undefined) {
    if (!Array.isArray(node.enum)) {
      push({
        path: `${path}.enum`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: '`enum` must be a list',
        explain: 'A list of the literal values the model may return, e.g. ["bullish", "neutral", "bearish"].',
      });
    } else if (node.enum.length === 0) {
      push({
        path: `${path}.enum`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: '`enum` needs at least one member',
        explain: 'An empty enum permits nothing, so every response fails the grammar. Drop the key instead.',
      });
    } else {
      const declared = types.filter((t) => t !== undefined);
      if (declared.length > 0) {
        const mismatched = node.enum.filter((member) => {
          const memberType = jsonTypeOf(member);
          if (declared.indexOf(memberType) > -1) return false;
          // An integer literal satisfies a `number` node; a fractional one does not satisfy
          // `integer`.
          return !(memberType === 'integer' && declared.indexOf('number') > -1);
        });
        if (mismatched.length > 0) {
          push({
            path: `${path}.enum`,
            severity: SEVERITY.ERROR,
            panel: PANEL.OUTPUT,
            title: `\`enum\` has ${mismatched.length} member${
              mismatched.length === 1 ? '' : 's'
            } that the declared type does not allow`,
            explain: `This node is \`${declared.join(' | ')}\`, so every member must be one too. Offending: ${mismatched
              .map((m) => JSON.stringify(m))
              .join(', ')}.`,
          });
        }
      }
    }
  }

  types.forEach((t) => {
    if (t !== undefined && SCHEMA_ALLOWED_TYPES.indexOf(t) === -1) {
      push({
        path: `${path}.type`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: `Type \`${t}\` is not allowed`,
        explain: `Allowed types are ${SCHEMA_ALLOWED_TYPES.join(', ')}.`,
      });
    }
  });

  // The engine branches on MEMBERSHIP (`if "object" in types`), not on the first entry, so a
  // nullable node like ["null","object"] still has to satisfy the object rules.
  if (types.indexOf('object') > -1) {
    const properties = node.properties;
    const propertyKeys = isObject(properties) ? Object.keys(properties) : [];
    if (propertyKeys.length === 0) {
      push({
        path: `${path}.properties`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: 'An object needs at least one property',
        explain: 'Strict mode rejects an object with empty `properties`.',
      });
    }
    const required = Array.isArray(node.required) ? node.required : [];
    const missing = propertyKeys.filter((k) => required.indexOf(k) === -1);
    if (missing.length > 0) {
      push({
        path: `${path}.required`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: 'Every property must be listed in `required`',
        explain: `Strict mode allows no optional keys. Missing: ${missing.join(', ')}.`,
      });
    }
    if (node.additionalProperties !== false) {
      push({
        path: `${path}.additionalProperties`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: '`additionalProperties` must be false',
        explain: 'Strict mode requires every object to close itself.',
      });
    }
    propertyKeys.forEach((key) => validateSchemaNode(properties[key], `${path}.properties.${key}`, push));
    return;
  }

  if (types.indexOf('array') > -1) {
    if (!isObject(node.items)) {
      push({
        path: `${path}.items`,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: 'An array must declare `items`',
        explain: 'Strict mode requires an items schema on every array.',
      });
      return;
    }
    validateSchemaNode(node.items, `${path}.items`, push);
  }
};

// ---------------------------------------------------------------------------
// document_set.documents
// ---------------------------------------------------------------------------
const validateDocTypeRules = (rules, path, push) => {
  if (!Array.isArray(rules) || rules.length === 0) {
    push({
      path,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: '`doc_types` must list at least one rule',
      explain: 'Each rule is `{ "doc_type": string, "latest_n"?: int > 0 }`.',
    });
    return;
  }

  const seen = {};
  rules.forEach((rule, i) => {
    const docType = isObject(rule) ? rule.doc_type : undefined;
    if (!docType) {
      push({
        path: `${path}.${i}.doc_type`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: 'A doc-type rule needs a `doc_type`',
        explain: 'Rule ' + (i + 1) + ' has no document type.',
      });
    } else if (typeof docType !== 'string') {
      // `doc_type: StrictStr` (filter.py:40, related_filter.py:59). A number is truthy, so it used
      // to sail past the check above and be rejected at lock instead.
      push({
        path: `${path}.${i}.doc_type`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: '`doc_type` must be a string',
        explain: 'The engine reads it as a strict string — a number or a boolean is a type error at lock.',
      });
    } else if (seen[docType]) {
      push({
        path: `${path}.${i}.doc_type`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `Duplicate doc_type \`${docType}\``,
        explain: 'Each document type may appear once. Merge the two rules and keep the latest_n you want.',
      });
    } else {
      seen[docType] = true;
    }

    // `latest_n` is `StrictInt | None` in the engine: null and omitted both mean "all matching".
    if (isObject(rule) && rule.latest_n !== undefined && rule.latest_n !== null) {
      // Tested RAW, not through Number(): the field is `StrictInt | None` (filter.py:41), so the
      // string '3' and the boolean true are type errors even though Number() turns them into 3 and
      // 1. Number.isInteger returns false for every non-number, so no typeof guard is needed.
      if (!Number.isInteger(rule.latest_n) || rule.latest_n <= 0) {
        push({
          path: `${path}.${i}.latest_n`,
          severity: SEVERITY.ERROR,
          panel: PANEL.DOCUMENT_SET,
          title: '`latest_n` must be an integer greater than 0',
          explain: 'Omit the key entirely to mean "every matching document".',
        });
      }
    }
  });
};

const validateDateRange = (range, path, provides, triggerKind, push) => {
  if (!isObject(range)) {
    push({
      path,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: 'This documents kind needs a `date_range`',
      explain: 'Both endpoints are inclusive. Use now±Nd, T±Nd with an anchor, or YYYY-MM-DD.',
    });
    return;
  }

  ['from', 'to'].forEach((endpoint) => {
    const token = range[endpoint];
    if (token === undefined || token === null || token === '') {
      push({
        path: `${path}.${endpoint}`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `\`${endpoint}\` is required`,
        explain: 'One of `now`, `now±Nd`, `T`, `T±Nd`, or `YYYY-MM-DD`.',
      });
    } else if (!isValidDateToken(token)) {
      push({
        path: `${path}.${endpoint}`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `\`${token}\` is not a date token`,
        explain: 'Allowed: `now`, `now-90d`, `now+7d`, `T`, `T-32d`, `T+2d`, `2026-01-01`.',
      });
    }
  });

  const anchored = isAnchorToken(range.from) || isAnchorToken(range.to);

  if (anchored && !range.anchor) {
    push({
      path: `${path}.anchor`,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: 'A `T` token requires an `anchor`',
      explain: 'The anchor names the trigger field the window hangs off, e.g. `trigger.target_release_date`.',
    });
  }

  if (!anchored && range.anchor) {
    push({
      path: `${path}.anchor`,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: '`anchor` is forbidden here',
      explain: 'Neither endpoint uses a `T` token, so there is nothing for the anchor to anchor.',
    });
  }

  if (range.anchor) {
    const field = String(range.anchor).replace(/^trigger\./, '');
    if (provides.indexOf(field) === -1) {
      push({
        path: `${path}.anchor`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `\`${range.anchor}\` is not provided by this trigger`,
        explain: `\`${triggerKind || 'the trigger'}\` provides ${
          provides.length ? provides.map((p) => `\`trigger.${p}\``).join(', ') : 'nothing'
        }. Pick one of those, or switch the window to now-relative.`,
      });
    }
  }

  if (isAbsoluteToken(range.from) && isAbsoluteToken(range.to) && range.from > range.to) {
    push({
      path,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: '`from` is after `to`',
      explain: 'Resolution enforces from <= to.',
    });
  }
};

const validateCompanyScope = (scope, path, push, strict) => {
  if (!isObject(scope)) return;

  if (scope.kind && !isKnown('company_scope', scope.kind)) {
    push({
      path: `${path}.kind`,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: `Unknown company scope \`${scope.kind}\``,
      explain: 'Only `peers_of_trigger` exists today.',
    });
  }

  // The two paths differ in VALUE rules, not in TYPES. Both models are strict-scalar:
  // related_filter's _CompaniesBlock (related_filter.py:83-85) and screen's PeersOfTriggerScope
  // .Params (company_scope.py:66-72) each declare depth StrictInt, include_self StrictBool and
  // relationship_kinds list[StrictStr]. Only related_filter adds the value validators — non-empty
  // and vocabulary on relationship_kinds, and _depth_one. So the type checks below run on both
  // paths and the value checks stay behind `strict`; screen really does accept depth: 2 and an
  // empty relationship_kinds list, and flagging those would reject a spec the loader takes.
  if (scope.relationship_kinds !== undefined) {
    const kinds = scope.relationship_kinds;
    if (!Array.isArray(kinds)) {
      push({
        path: `${path}.relationship_kinds`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: '`relationship_kinds` must be a list',
        explain: `The engine types it \`list[StrictStr]\` — a bare string or a null fails at lock.`,
      });
    } else {
      kinds.forEach((k, i) => {
        if (typeof k !== 'string') {
          push({
            path: `${path}.relationship_kinds.${i}`,
            severity: SEVERITY.ERROR,
            panel: PANEL.DOCUMENT_SET,
            title: 'A relationship kind must be a string',
            explain: 'The list is `list[StrictStr]`, so a number or a boolean inside it is a type error at lock.',
          });
        }
      });

      if (strict && kinds.length === 0) {
        push({
          path: `${path}.relationship_kinds`,
          severity: SEVERITY.ERROR,
          panel: PANEL.DOCUMENT_SET,
          title: 'Pick at least one relationship kind',
          explain: `A non-empty subset of ${RELATIONSHIP_KINDS.join(', ')}.`,
        });
      }
      if (strict) {
        kinds.forEach((k) => {
          if (typeof k === 'string' && RELATIONSHIP_KINDS.indexOf(k) === -1) {
            push({
              path: `${path}.relationship_kinds`,
              severity: SEVERITY.ERROR,
              panel: PANEL.DOCUMENT_SET,
              title: `\`${k}\` is not a relationship kind`,
              explain: `Allowed: ${RELATIONSHIP_KINDS.join(', ')}.`,
            });
          }
        });
      }
    }
  }

  if (scope.include_self !== undefined && typeof scope.include_self !== 'boolean') {
    // Guarded on presence: include_self is optional with a default of false, so an unguarded test
    // would flag every legal spec that omits it.
    push({
      path: `${path}.include_self`,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: '`include_self` must be true or false',
      explain: 'The engine reads it as a strict boolean, so the strings "yes" and "true" fail at lock.',
    });
  }

  if (scope.depth !== undefined && !Number.isInteger(scope.depth)) {
    push({
      path: `${path}.depth`,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: '`depth` must be a whole number',
      explain: 'The engine reads it as a strict integer — the string "1" is a type error at lock.',
    });
  } else if (strict && scope.depth !== undefined && scope.depth !== 1) {
    push({
      path: `${path}.depth`,
      severity: SEVERITY.ERROR,
      panel: PANEL.DOCUMENT_SET,
      title: '`depth` must be 1',
      explain: 'The engine accepts a depth of 1 only.',
    });
  }
};

// ---------------------------------------------------------------------------
// the entry point
// ---------------------------------------------------------------------------
export const validateDefinition = (definition) => {
  const problems = [];
  // Lock is the default tier: it is what this file has always meant by an error, so every existing
  // rule keeps its meaning and only the CRUD-tier rules have to say so.
  const push = (p) => problems.push({ tier: TIER.LOCK, ...p });
  const d = isObject(definition) ? definition : {};

  // `version: StrictInt` (specs_loader.py:392) is enforced at model_validate, which runs BEFORE the
  // `_v<N>` suffix comparison at :892 — so a wrongly-TYPED version never reaches the suffix check
  // and must not be reported as the wrong NUMBER as well. Absent is not a type error: that case is
  // still reported by the suffix branch, which is where it has always been reported.
  const versionTypeError = d.version !== undefined && !Number.isInteger(d.version);

  // ---- identity -----------------------------------------------------------
  const id = d.score_spec_id;
  const idMatch = SPEC_ID_REGEX.exec(String(id || ''));

  if (!id) {
    push({
      path: 'score_spec_id',
      severity: SEVERITY.ERROR,
      panel: PANEL.IDENTITY,
      title: 'A spec needs an id',
      explain: 'Lowercase, ending in `_v<N>` — e.g. `share_buyback_mentions_v1`.',
    });
  } else if (FEED_NAME_SEPARATOR.test(String(id))) {
    // Separate from the pattern check, because the pattern allows `_` and therefore allows `__`.
    // The published feed is `score_spec_<id>[__<name>]`, so a `__` inside the id makes reverse
    // resolution ambiguous. Lock tier: the loader's cross-field pass is where it is enforced.
    push({
      path: 'score_spec_id',
      severity: SEVERITY.ERROR,
      tier: TIER.LOCK,
      panel: PANEL.IDENTITY,
      title: `\`${id}\` cannot contain \`__\``,
      explain:
        'A double underscore is the separator a derived multi-score feed name uses, so an id containing one makes `score_spec_<id>__<name>` ambiguous to resolve back to a spec and score. Use single underscores.',
    });
  } else if (!idMatch) {
    push({
      path: 'score_spec_id',
      severity: SEVERITY.ERROR,
      panel: PANEL.IDENTITY,
      title: `\`${id}\` is not a valid spec id`,
      explain: 'Must match `^[a-z][a-z0-9_]*_v(\\d+)$` — lowercase, and it must end in `_v<N>`.',
    });
  } else if (!versionTypeError && Number(idMatch[1]) !== Number(d.version)) {
    push({
      path: 'version',
      severity: SEVERITY.ERROR,
      panel: PANEL.IDENTITY,
      title: `\`version\` must be ${idMatch[1]}`,
      explain: `The id ends in \`_v${idMatch[1]}\`, so version has to match it.`,
    });
  }

  // Outside the chain above, so a malformed id does not swallow it — the loader reports this one
  // first either way. Number.isInteger is false for every non-number, so it covers "1", true and
  // null with no typeof guard; JSON.stringify collapses 1.0 to 1, so the float case is invisible
  // from here and only the server can catch it.
  if (versionTypeError) {
    push({
      path: 'version',
      severity: SEVERITY.ERROR,
      panel: PANEL.IDENTITY,
      title: '`version` must be a whole number',
      explain:
        'The compile reads it as a strict integer, so a quoted `"1"`, a boolean or a null is a type error at lock — even when the number itself looks right.',
    });
  }

  if (!d.description) {
    push({
      path: 'description',
      severity: SEVERITY.ERROR,
      panel: PANEL.IDENTITY,
      title: 'A description is required',
      explain: 'One line, human-readable. It is what the list and the backfill picker show.',
    });
  }

  Object.keys(d).forEach((key) => {
    if (TOP_LEVEL_KEYS.indexOf(key) === -1) {
      push({
        path: key,
        severity: SEVERITY.ERROR,
        panel: PANEL.IDENTITY,
        title: `Unknown top-level key \`${key}\``,
        explain:
          'The compile rejects unknown top-level keys (`extra: forbid`). It saves fine today and then can never lock.',
      });
    }
  });

  // `source_kind` is a legal key (see TOP_LEVEL_KEYS) but not a free one — it selects WHICH schema
  // validates the row, and this editor authors score specs. Omitting it is the normal case and the
  // one every shipped spec uses; the loader defaults it. Both bad values are ERROR because both are
  // refusals at lock, but they come from different places and deserve different words.
  if (d.source_kind !== undefined && d.source_kind !== SCORE_SPEC_SOURCE_KIND) {
    const known = SOURCE_KINDS.indexOf(d.source_kind) > -1;
    const shown = typeof d.source_kind === 'string' ? d.source_kind : JSON.stringify(d.source_kind);
    push({
      path: 'source_kind',
      severity: SEVERITY.ERROR,
      panel: PANEL.IDENTITY,
      title: known ? 'A `derived_feed` cannot be compiled as a score spec' : `\`${shown}\` is not a source_kind`,
      explain: known
        ? 'A derived feed is a different row shape — feed_name and builder instead of the scoring blocks — so the compile refuses it before validating anything else. This editor cannot lock it. Set `score_spec`, or drop the key and take the default.'
        : `The loader reads this off the raw row before validation and accepts only ${SOURCE_KINDS.map(
            (k) => `\`${k}\``
          ).join(' or ')}. Drop the key to take the default, \`${SCORE_SPEC_SOURCE_KIND}\`.`,
    });
  }

  // `enabled_in` was REMOVED from the format (PR #455, Change 5). Both compile paths strip it
  // before model_validate, which is why the old list[StrictStr] type and vocabulary rows are gone:
  // there is nothing left to type-check, and a mirror that rejects a value the engine no longer
  // reads is stricter than the engine — the one failure mode this file must not have.
  //
  // It stays in TOP_LEVEL_KEYS so it is not reported as an unknown key, and it stays ADVICE rather
  // than silence because GET echoes spec_json verbatim: a pre-existing row still SHOWS the key, and
  // an operator has no way to tell a live setting from a dead one. `toPayload` drops it on save.
  if (d.enabled_in !== undefined) {
    push({
      path: 'enabled_in',
      severity: SEVERITY.ADVICE,
      panel: PANEL.IDENTITY,
      title: '`enabled_in` was removed from the spec format',
      explain:
        'It is stripped at load, so it decides nothing — environment placement is per-store, and live firing is the `live_enabled` column. Saving this draft drops the key.',
    });
  }

  // ---- trigger ------------------------------------------------------------
  const trigger = isObject(d.trigger) ? d.trigger : {};
  const triggerKind = trigger.kind;

  if (!triggerKind) {
    push({
      path: 'trigger.kind',
      severity: SEVERITY.ERROR,
      panel: PANEL.TRIGGER,
      title: 'Pick when this spec runs',
      explain: 'A spec needs a trigger — it is what decides when the score is produced.',
    });
  } else if (!isKnown('trigger', triggerKind)) {
    push({
      path: 'trigger.kind',
      severity: SEVERITY.ERROR,
      panel: PANEL.TRIGGER,
      title: `Unknown trigger kind \`${triggerKind}\``,
      explain:
        'Not in the trigger registry as this UI knows it. If the backend just added it, the compile is the authority — check the draft.',
    });
  } else if (!isSupported('trigger', triggerKind)) {
    const entry = kindEntry('trigger', triggerKind);
    push({
      path: 'trigger.kind',
      severity: SEVERITY.ERROR,
      panel: PANEL.TRIGGER,
      title: `\`${triggerKind}\` is not an available trigger`,
      explain: entry.caveat,
    });
  }

  if (triggerKind === 'document_available') {
    const docTypes = trigger.doc_types;
    if (!Array.isArray(docTypes)) {
      push({
        path: 'trigger.doc_types',
        severity: SEVERITY.ERROR,
        panel: PANEL.TRIGGER,
        title: '`doc_types` must be a list',
        explain: 'The document types whose arrival fires the trigger.',
      });
    } else if (docTypes.length === 0) {
      // The trigger's Params model has no non-empty validator, unlike screen's — so this compiles
      // and locks. It just never fires, which is worth saying and not worth calling an error.
      push({
        path: 'trigger.doc_types',
        severity: SEVERITY.ADVICE,
        panel: PANEL.TRIGGER,
        title: 'No document types listed, so this trigger never fires',
        explain: 'The loader accepts an empty list — nothing will match it, so the spec produces no scores.',
      });
    }
  }

  if (triggerKind === 'pre_earnings_release') {
    const lead = trigger.lead_time_days;
    // Tested RAW. `lead_time_days: StrictInt` is required (pre_earnings_release.py:45), and
    // Number() laundered every way of getting it wrong: Number(null), Number('') and Number(false)
    // are all 0, and Number('2') is 2. The null is not hypothetical — NumberField emits null for an
    // emptied box, so clearing the control used to make the error DISAPPEAR.
    if (!Number.isInteger(lead)) {
      push({
        path: 'trigger.lead_time_days',
        severity: SEVERITY.ERROR,
        panel: PANEL.TRIGGER,
        title: lead === undefined ? '`lead_time_days` is required' : '`lead_time_days` must be a whole number of days',
        explain:
          'How many days before the release the spec fires. The engine reads it as a strict integer, so an empty field, a null or a quoted "2" is a type error at lock.',
      });
    }
  }

  const provides = providesFor(triggerKind);

  // ---- document_set -------------------------------------------------------
  const documentSet = isObject(d.document_set) ? d.document_set : {};

  [
    ['companies', 'companies'],
    ['documents', 'documents'],
    ['fanout', 'fanout'],
  ].forEach(([axis, key]) => {
    const node = isObject(documentSet[key]) ? documentSet[key] : {};
    const kind = node.kind;
    const path = `document_set.${key}.kind`;

    if (!kind) {
      push({
        path,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `\`document_set.${key}\` needs a kind`,
        explain: 'All three axes are required.',
      });
      return;
    }
    if (!isKnown(axis, kind)) {
      push({
        path,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `Unknown ${key} kind \`${kind}\``,
        explain:
          'Not in this axis’s registry as this UI knows it. If the backend just added it, check the draft against the real compiler.',
      });
      return;
    }
    if (!isSupported(axis, kind)) {
      push({
        path,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `\`${kind}\` is not an available ${key} kind`,
        explain: kindEntry(axis, kind).caveat,
      });
      return;
    }

    (kindEntry(axis, kind).requires || []).forEach((requirement) => {
      if (provides.indexOf(requirement) === -1) {
        push({
          path,
          severity: SEVERITY.ERROR,
          panel: PANEL.DOCUMENT_SET,
          title: `\`${kind}\` needs \`${requirement}\``,
          explain: `\`${triggerKind || 'the trigger'}\` provides ${
            provides.length ? provides.map((p) => `\`${p}\``).join(', ') : 'nothing'
          }, so this axis has nothing to resolve from.`,
        });
      }
    });
  });

  const documents = isObject(documentSet.documents) ? documentSet.documents : {};
  const documentsKind = documents.kind;

  // Each documents resolver's Params model is `extra: "forbid"`, and the loader passes every
  // non-`kind` key straight into it. A param left over from a previously-selected kind is not
  // inert — it makes the spec uncompilable, and it is invisible in the form because the form only
  // renders the current kind's controls. This is the same trap UnknownRootKeysStrip catches at the
  // top level, one level down.
  if (DOCUMENT_KIND_PARAMS[documentsKind]) {
    const allowed = DOCUMENT_KIND_PARAMS[documentsKind];
    Object.keys(documents).forEach((key) => {
      if (key === 'kind' || allowed.indexOf(key) > -1) return;
      push({
        path: `document_set.documents.${key}`,
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: `\`${key}\` does not belong to documents.${documentsKind}`,
        explain: `That kind accepts ${
          allowed.length ? allowed.join(', ') : 'no parameters'
        }. Leftover keys from another kind are rejected by the compile (\`extra: forbid\`) and are not shown by the form.`,
      });
    });
  }

  if (KINDS_WITH_DOC_TYPE_RULES.indexOf(documentsKind) > -1) {
    validateDocTypeRules(documents.doc_types, 'document_set.documents.doc_types', push);
  }

  if (KINDS_WITH_DOC_TYPE_STRINGS.indexOf(documentsKind) > -1) {
    const docTypes = documents.doc_types;
    if (!Array.isArray(docTypes) || docTypes.length === 0) {
      push({
        path: 'document_set.documents.doc_types',
        severity: SEVERITY.ERROR,
        panel: PANEL.DOCUMENT_SET,
        title: 'List at least one document type to search over',
        explain: 'A screen searches a non-empty set of document types.',
      });
    } else {
      docTypes.forEach((entry, i) => {
        if (typeof entry !== 'string') {
          push({
            path: `document_set.documents.doc_types.${i}`,
            severity: SEVERITY.ERROR,
            panel: PANEL.DOCUMENT_SET,
            title: 'A screen document type must be a plain string',
            explain: 'Unlike filter and related_filter, screen takes bare type names with no per-type latest_n.',
          });
        }
      });
    }
  }

  if (documentsKind === 'screen') {
    push({
      path: 'document_set.documents.kind',
      severity: SEVERITY.ADVICE,
      panel: PANEL.DOCUMENT_SET,
      title: '`screen` is in development and slow',
      explain:
        'Broad retrieval is imprecise, and `restrict_to_companies` does not actually bound it. `filter` is the fully operational path; `related_filter` is experimental, so neither is a drop-in recommendation.',
    });
    validateCompanyScope(documents.restrict_to_companies, 'document_set.documents.restrict_to_companies', push, false);
  }

  if (KINDS_WITH_DATE_RANGE.indexOf(documentsKind) > -1) {
    validateDateRange(documents.date_range, 'document_set.documents.date_range', provides, triggerKind, push);
  }

  if (documentsKind === 'related_filter') {
    // Advice, not an error: the loader accepts this kind and the engine runs it. What it cannot
    // promise is lookahead safety, and a spec authored today will not learn that anywhere else.
    push({
      path: 'document_set.documents.kind',
      severity: SEVERITY.ADVICE,
      panel: PANEL.DOCUMENT_SET,
      title: '`related_filter` is experimental and not lookahead-safe',
      explain:
        'The related-company dataset is not point-in-time, so a backfill can read relationships that did not exist on the occasion date. The run succeeds and the scores are quietly contaminated. `filter` is the fully operational path.',
    });
    validateCompanyScope(documents.companies, 'document_set.documents.companies', push, true);
  }

  // ---- score_gen ----------------------------------------------------------
  const scoreGen = isObject(d.score_gen) ? d.score_gen : {};

  if (!scoreGen.model) {
    push({
      path: 'score_gen.model',
      severity: SEVERITY.ERROR,
      panel: PANEL.SCORE_GEN,
      title: 'A model is required',
      explain:
        'The compile checks it against the engine’s price table — this editor cannot, so the name is not verified here.',
    });
  }

  if (!scoreGen.prompt) {
    push({
      path: 'score_gen.prompt',
      severity: SEVERITY.ERROR,
      panel: PANEL.SCORE_GEN,
      title: 'A prompt is required',
      explain: 'The question the model is asked. It is a Jinja2 template.',
    });
  }

  // The loader COMPILES both templates but cannot know the namespace they will be rendered against,
  // so a variable that does not exist for this documents kind locks cleanly and then kills every
  // unit of every run with template_render_failed. ADVICE, not ERROR, because lock really does
  // succeed — the failure is at run time. Lives here as well as in the prompt editor so a collapsed
  // score_gen panel cannot hide it.
  const runVariables = runVariablesFor(documentsKind);
  if (runVariables) {
    ['prompt', 'format_prompt'].forEach((key) => {
      const unknown = unknownVariables(scoreGen[key], runVariables);
      if (unknown.length === 0) return;
      push({
        path: `score_gen.${key}`,
        severity: SEVERITY.ADVICE,
        panel: PANEL.SCORE_GEN,
        title: `${unknown.map((v) => `\`{{${v}}}\``).join(', ')} ${
          unknown.length === 1 ? 'is' : 'are'
        } not in a \`${documentsKind}\` run’s namespace`,
        explain: `The engine builds the namespace from the documents kind and renders with StrictUndefined, so this raises rather than rendering empty — every unit fails permanently with template_render_failed. A \`${documentsKind}\` run provides ${runVariables.join(
          ', '
        )}.`,
      });
    });
  }

  // `score_gen.parallelize` was REMOVED from the format (PR #455, Change 4), and with it the old
  // rule that `related_filter` required it to be true. The engine always parallelizes and strips
  // the key at load, so — exactly as with `enabled_in` above — there is no lock-time failure left to
  // mirror, only a dead key a legacy row still displays.
  if (scoreGen.parallelize !== undefined) {
    push({
      path: 'score_gen.parallelize',
      severity: SEVERITY.ADVICE,
      panel: PANEL.SCORE_GEN,
      title: '`score_gen.parallelize` was removed from the spec format',
      explain:
        'The engine always parallelizes; the serial debug mode and the `related_filter` requirement are both gone. The key is stripped at load, and saving this draft drops it.',
    });
  }

  if (scoreGen.reasoning_effort !== undefined && scoreGen.reasoning_effort !== null) {
    if (REASONING_EFFORTS.indexOf(scoreGen.reasoning_effort) === -1) {
      push({
        path: 'score_gen.reasoning_effort',
        severity: SEVERITY.ERROR,
        panel: PANEL.SCORE_GEN,
        title: `\`${scoreGen.reasoning_effort}\` is not a reasoning effort`,
        explain: `One of ${REASONING_EFFORTS.join(', ')}, or omit the key to keep the engine default (medium).`,
      });
    }
  }

  ['max_context_chunks', 'max_context_tokens', 'max_output_tokens'].forEach((key) => {
    if (scoreGen[key] === undefined || scoreGen[key] === null || scoreGen[key] === '') {
      push({
        path: `score_gen.${key}`,
        severity: SEVERITY.ERROR,
        panel: PANEL.SCORE_GEN,
        title: `\`${key}\` is required`,
        explain: 'The loader has no default for the three budget fields.',
      });
    }
  });

  const maxOutput = Number(scoreGen.max_output_tokens);
  if (Number.isFinite(maxOutput) && maxOutput > 0 && maxOutput < 3000) {
    push({
      path: 'score_gen.max_output_tokens',
      severity: SEVERITY.ADVICE,
      panel: PANEL.SCORE_GEN,
      title: 'This may be too low for a reasoning model',
      explain:
        'House advice, NOT a rule the loader enforces: hidden reasoning tokens count against this budget, and too low a value yields empty completions and a re-score tax. A multi-section structured output needs far more.',
    });
  }

  if (scoreGen.context_settings !== undefined && scoreGen.context_settings !== null) {
    const settings = scoreGen.context_settings;
    if (!isObject(settings)) {
      push({
        path: 'score_gen.context_settings',
        severity: SEVERITY.ERROR,
        panel: PANEL.SCORE_GEN,
        title: '`context_settings` must be an object',
        explain: 'A map of known keys to "yes" / "no" / "auto".',
      });
    } else {
      Object.keys(settings).forEach((key) => {
        if (CONTEXT_SETTING_KEYS.indexOf(key) === -1) {
          push({
            path: `score_gen.context_settings.${key}`,
            severity: SEVERITY.ERROR,
            panel: PANEL.SCORE_GEN,
            title: `\`${key}\` is not a context setting`,
            explain: 'Only the eleven known toggles are accepted.',
          });
        } else if (CONTEXT_SETTING_VALUES.indexOf(settings[key]) === -1) {
          push({
            path: `score_gen.context_settings.${key}`,
            severity: SEVERITY.ERROR,
            panel: PANEL.SCORE_GEN,
            title: `\`${String(settings[key])}\` is not a valid value`,
            explain: `Must be one of ${CONTEXT_SETTING_VALUES.join(', ')}.`,
          });
        }
      });
    }
  }

  // ---- output_extraction --------------------------------------------------
  const output = isObject(d.output_extraction) ? d.output_extraction : {};
  const hasSingle =
    output.score_jsonpath !== undefined && output.score_jsonpath !== null && output.score_jsonpath !== '';
  const hasMulti = Array.isArray(output.scores);

  // `score_jsonpath` is strict-on-write / tolerant-on-load: the CREATE/PATCH API returns 422 for any
  // body containing it, while the loader still desugars it so the frozen pre-multi-score specs keep
  // loading. So a saved legacy spec is not broken — but it cannot be saved again until the key goes,
  // which makes this a SAVE-tier row on a spec that locks perfectly well.
  if (hasSingle) {
    push({
      path: 'output_extraction.score_jsonpath',
      severity: SEVERITY.ERROR,
      tier: TIER.SAVE,
      panel: PANEL.OUTPUT,
      title: '`score_jsonpath` is rejected on write',
      explain:
        'Create and PATCH 422 any body containing it. The loader still accepts it, so a spec already stored this way keeps running — but this draft cannot be saved until it is rewritten as a one-entry `scores` list named `score`, which keeps the feed’s bare `score_spec_<id>` name.',
    });
  }

  if (hasSingle && hasMulti) {
    push({
      path: 'output_extraction',
      severity: SEVERITY.ERROR,
      panel: PANEL.OUTPUT,
      title: 'Set `score_jsonpath` or `scores`, not both',
      explain: 'They are mutually exclusive. `score_jsonpath` is the retired one-score shorthand.',
    });
  } else if (!hasSingle && !hasMulti) {
    push({
      path: 'output_extraction',
      severity: SEVERITY.ERROR,
      panel: PANEL.OUTPUT,
      title: 'Every spec extracts at least one score',
      explain:
        'Declare a `scores` list — one entry for a single score, several for several. Neither key is set, so this spec saves cleanly and can never lock.',
    });
  }

  // ---- the output contract (PR #455, Change 1) ----------------------------
  // Declared exactly once: output_extraction.output_schema (structured outputs) OR
  // score_gen.format_prompt (prose, for a model with no structured-output support).
  //
  // An empty or whitespace-only format_prompt counts as ABSENT, matching the server — which is what
  // lets the radio in question 4 record the operator's choice as a present-but-empty key without
  // that key satisfying the contract.
  const hasFormatPrompt = typeof scoreGen.format_prompt === 'string' && scoreGen.format_prompt.trim() !== '';
  // `"output_schema": null` is explicitly treated as absent by the API, so a prose spec may send the
  // key as null without tripping the check.
  const hasOutputSchema = output.output_schema !== undefined && output.output_schema !== null;

  if (!hasFormatPrompt && !hasOutputSchema) {
    push({
      path: 'output_extraction.output_schema',
      severity: SEVERITY.ERROR,
      tier: TIER.SAVE,
      panel: PANEL.OUTPUT,
      title: 'Declare an output contract',
      explain:
        'Exactly one of `output_extraction.output_schema` (structured outputs) or `score_gen.format_prompt` (prose fallback). Neither is set, so create and PATCH reject this with a 422 — and the loader rejects it at lock as well.',
    });
  } else if (hasFormatPrompt && hasOutputSchema) {
    push({
      path: 'score_gen.format_prompt',
      severity: SEVERITY.ERROR,
      tier: TIER.SAVE,
      panel: PANEL.OUTPUT,
      title: 'Two output contracts are declared',
      explain:
        'A spec declares its output shape once. This is NOT a compile failure — the loader tolerates both so legacy specs keep loading — but create and PATCH now 422, so this draft cannot be saved until one of them goes. Keep the schema and move guidance a schema cannot express (numeric ranges, rubrics) into `score_gen.prompt`, or keep the prose and drop the schema.',
    });
  }

  // A spec whose declared contract is prose. Advice while `format_prompt` is merely deprecated, a
  // save-tier error once it is retired — see proseContract.js for why the client waits for the API.
  // Deliberately NOT filed when both are declared: that spec has a louder problem already, and its
  // fix (drop the prose) is the same one.
  if (hasFormatPrompt && !hasOutputSchema) {
    const row = proseDeprecationRow();
    push({
      path: 'score_gen.format_prompt',
      severity: isProseRetired() ? SEVERITY.ERROR : SEVERITY.ADVICE,
      tier: isProseRetired() ? TIER.SAVE : undefined,
      panel: PANEL.OUTPUT,
      title: row.title,
      explain: row.explain,
    });
  }

  const schema = isObject(output.output_schema) ? output.output_schema : null;

  if (output.output_schema !== undefined && output.output_schema !== null) {
    if (!isObject(output.output_schema)) {
      push({
        path: 'output_extraction.output_schema',
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: '`output_schema` must be an object',
        explain: 'A bare JSON Schema describing the model’s output object.',
      });
    } else {
      const topType = output.output_schema.type;
      const topIsObject = topType === 'object' || (Array.isArray(topType) && topType.indexOf('object') > -1);
      if (!topIsObject) {
        push({
          path: 'output_extraction.output_schema.type',
          severity: SEVERITY.ERROR,
          panel: PANEL.OUTPUT,
          title: 'The top level must be `type: object`',
          explain: 'The response processor wraps single objects; arrays of objects are legacy.',
        });
      }
      validateSchemaNode(output.output_schema, 'output_extraction.output_schema', push);
    }
  }

  // Coherence: scores must resolve to exactly one node of the declared shape.
  const coherence = (expr, path, exactlyOne, label) => {
    if (!schema || !expr) return;
    const { supported, matches } = matchCount(expr, schema);
    if (!supported) return; // outside our subset — abstain rather than assert
    if (exactlyOne && matches !== 1) {
      push({
        path,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: `\`${expr}\` resolves to ${matches} nodes of the declared schema`,
        explain: `${label} must resolve to exactly one node. As written it would fail extraction on every document.`,
      });
    }
    if (!exactlyOne && matches < 1) {
      push({
        path,
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: `\`${expr}\` resolves to no node of the declared schema`,
        explain: `${label} would be null on every run, which is always a bug.`,
      });
    }
  };

  if (hasSingle) coherence(output.score_jsonpath, 'output_extraction.score_jsonpath', true, 'A score path');

  if (hasMulti) {
    const scores = output.scores;
    if (scores.length === 0) {
      push({
        path: 'output_extraction.scores',
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: 'Declare at least one score',
        explain: 'An empty `scores` list extracts nothing.',
      });
    }
    if (scores.length > 1 && !schema) {
      push({
        path: 'output_extraction.output_schema',
        severity: SEVERITY.ERROR,
        panel: PANEL.OUTPUT,
        title: 'More than one score requires an `output_schema`',
        explain:
          'Multi-score extraction is all-or-nothing, so the shape has to be guaranteed rather than requested in prose.',
      });
    }

    const seen = {};
    scores.forEach((score, i) => {
      const name = isObject(score) ? score.name : undefined;
      const path = `output_extraction.scores.${i}.name`;
      if (!name) {
        push({
          path,
          severity: SEVERITY.ERROR,
          panel: PANEL.OUTPUT,
          title: 'Every score needs a name',
          explain: 'The name becomes the published feed’s `__<name>` suffix and is permanent at lock.',
        });
      } else {
        if (!SCORE_NAME_REGEX.test(name)) {
          push({
            path,
            severity: SEVERITY.ERROR,
            panel: PANEL.OUTPUT,
            title: `\`${name}\` is not a valid score name`,
            explain: 'Lowercase letters, digits and underscores only — it is an S3 / NATS / feed token.',
          });
        }
        if (RESERVED_SCORE_NAME_REGEX.test(name)) {
          push({
            path,
            severity: SEVERITY.ERROR,
            panel: PANEL.OUTPUT,
            title: `\`${name}\` is reserved`,
            explain: 'Names of the form `<digits>runs` collide with the multi-run feed suffix.',
          });
        }
        if (RESERVED_FEED_COLUMNS.indexOf(name) > -1) {
          push({
            path,
            severity: SEVERITY.ERROR,
            tier: TIER.LOCK,
            panel: PANEL.OUTPUT,
            title: `\`${name}\` is a reserved feed column`,
            explain: `The export already carries a \`${name}\` column, so a score of that name would clash with it. Reserved: ${RESERVED_FEED_COLUMNS.join(
              ', '
            )}.`,
          });
        }
        if (seen[name]) {
          push({
            path,
            severity: SEVERITY.ERROR,
            panel: PANEL.OUTPUT,
            title: `Duplicate score name \`${name}\``,
            explain: 'Names are unique within a spec.',
          });
        }
        seen[name] = true;
      }

      coherence(
        isObject(score) ? score.jsonpath : undefined,
        `output_extraction.scores.${i}.jsonpath`,
        true,
        'A score path'
      );

      // `feed_name` (PR #459, Change 3) overrides the derived feed name for this score. Validated
      // like a score name and for the same reason — it becomes an S3 key component and a NATS
      // subject token — but the check lives in the engine's STRICT tier, so a bad value saves
      // cleanly and dies at preview_score or lock. Which makes it a lock-tier ERROR here.
      const feedName = isObject(score) ? score.feed_name : undefined;
      if (feedName !== undefined && feedName !== null && feedName !== '') {
        const feedPath = `output_extraction.scores.${i}.feed_name`;
        if (typeof feedName !== 'string') {
          push({
            path: feedPath,
            severity: SEVERITY.ERROR,
            panel: PANEL.OUTPUT,
            title: '`feed_name` must be a string',
            explain: 'It is the suffix of a feed id — the system prepends `score_spec_`.',
          });
        } else {
          if (!FEED_NAME_REGEX.test(feedName)) {
            push({
              path: feedPath,
              severity: SEVERITY.ERROR,
              panel: PANEL.OUTPUT,
              title: `\`${feedName}\` is not a valid feed name`,
              explain:
                'Lowercase letters, digits and underscores only. Give the suffix alone — the system prepends `score_spec_`.',
            });
          } else if (FEED_NAME_SEPARATOR.test(feedName)) {
            push({
              path: feedPath,
              severity: SEVERITY.ERROR,
              panel: PANEL.OUTPUT,
              title: `\`${feedName}\` cannot contain \`__\``,
              explain: 'A double underscore is the separator a derived multi-score feed name uses.',
            });
          }
          if (RESERVED_SCORE_NAME_REGEX.test(feedName)) {
            push({
              path: feedPath,
              severity: SEVERITY.ERROR,
              panel: PANEL.OUTPUT,
              title: `\`${feedName}\` is reserved`,
              explain: 'Names of the form `<digits>runs` collide with the multi-run feed suffix.',
            });
          }
        }
      }

      // Per-score extra columns (PR #456, Change 2). Same shape as the spec-level map, different
      // destination: these land in this score's child factor_score_values.signal_payload and are
      // visible on that score's feed only, where the spec-level ones broadcast to all of them.
      //
      // A per-score key may not shadow a spec-level one — create/PATCH reject it — so this is a
      // SAVE-tier row, not a compile failure. Two different scores may share a key: each writes to
      // its own child row, so there is nothing to shadow.
      const scoreExtras = isObject(score) && isObject(score.extra_columns) ? score.extra_columns : null;
      if (scoreExtras) {
        const specLevelNames = isObject(output.extra_columns) ? Object.keys(output.extra_columns) : [];
        const shadowed = Object.keys(scoreExtras).filter((key) => specLevelNames.indexOf(key) > -1);
        if (shadowed.length > 0) {
          push({
            path: `output_extraction.scores.${i}.extra_columns`,
            severity: SEVERITY.ERROR,
            tier: TIER.SAVE,
            panel: PANEL.OUTPUT,
            title: `Score \`${name || i}\` reuses ${shadowed.length === 1 ? 'a' : ''} spec-level column name${
              shadowed.length === 1 ? '' : 's'
            }`,
            explain: `${shadowed
              .map((k) => `\`${k}\``)
              .join(
                ', '
              )} already declared in \`output_extraction.extra_columns\`. A per-score column may not shadow a spec-level one — rename one of them.`,
          });
        }
        Object.keys(scoreExtras).forEach((columnName) => {
          coherence(
            scoreExtras[columnName],
            `output_extraction.scores.${i}.extra_columns.${columnName}`,
            false,
            `The extra column \`${columnName}\``
          );
        });
      }
    });
  }

  if (isObject(output.extra_columns)) {
    Object.keys(output.extra_columns).forEach((name) => {
      // Not an error anywhere: the column is accepted and then dropped from the payload — on the
      // publish path, in snapshot.build_frames, and at view time — so it cannot overwrite the
      // point-in-time row column of the same name. Advice, because the loss is otherwise silent.
      if (RESERVED_FEED_COLUMNS.indexOf(name) > -1) {
        push({
          path: `output_extraction.extra_columns.${name}`,
          severity: SEVERITY.ADVICE,
          panel: PANEL.OUTPUT,
          title: `\`${name}\` is dropped from the payload`,
          explain: `The feed already carries a \`${name}\` column, so this one is stripped rather than allowed to overwrite it. It saves, it locks, and the value simply never appears — rename it to keep it.`,
        });
      }
      coherence(
        output.extra_columns[name],
        `output_extraction.extra_columns.${name}`,
        false,
        `The extra column \`${name}\``
      );
    });
  }

  return problems;
};

export const countErrors = (problems) => problems.filter((p) => p.severity === SEVERITY.ERROR).length;

// The two gates, counted separately. A save-tier row stops the draft from being stored at all, so a
// count that lumped it in with "would fail the compile at lock" would name the wrong door.
export const countSaveBlockers = (problems) =>
  problems.filter((p) => p.severity === SEVERITY.ERROR && p.tier === TIER.SAVE).length;
export const countLockErrors = (problems) =>
  problems.filter((p) => p.severity === SEVERITY.ERROR && p.tier !== TIER.SAVE).length;

// Problems owned by one question panel, for the per-panel header badge. Identity rows belong to
// the identity strip, not to a panel.
export const problemsForPanel = (problems, panel) => problems.filter((p) => p.panel === panel);
